<template>
  <section class="sale-documents-tab">
    <DocumentsTableCard
      :rows="documentsRows"
      :loading="loading"
      :show-download-all="showDownloadAllButton"
      @download-all="handleDownloadAll"
      @open-create="openCreateModal"
      @download-item="handleDownloadItem"
      @edit-item="handleEditItem"
      @delete-item="handleDeleteItem"
    />

    <DocumentsCreateModal
      :show="showCreateModal"
      :mode="documentModalMode"
      :initial-data="editingDocument"
      @close="showCreateModal = false"
      @submit="handleCreateDocument"
    />

    <ConfirmActionModal
      :show="isDeleteConfirmModalOpen"
      :title="text.confirmDelete.title"
      :subtitle="text.confirmDelete.subtitle"
      :message="deleteConfirmMessage"
      :confirm-text="text.confirmDelete.confirm"
      :cancel-text="text.confirmDelete.cancel"
      @cancel="closeDeleteConfirmModal"
      @confirm="confirmDeleteItem"
    />
  </section>
</template>

<script>
import DocumentsTableCard from '@/components/documents/DocumentsTableCard.vue';
import DocumentsCreateModal from '@/components/documents/DocumentsCreateModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import salesService from '@/services/sales';
import salesMultiCustomerService from '@/services/salesMultiCustomer';
import {
  extractApiErrorMessage,
  extractPayload,
} from '@/utils/dataNormalization';
import { downloadBlobFile } from '@/utils/fileDownload';
import { formatDateShort } from '@/utils/datePresentation';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  buildDocumentDownloadTimestamp,
  isPdfDocument,
  normalizeDocumentDownloadNameSegment,
  openDocumentInNewTab,
  resolveDocumentDownloadErrorMessage,
  resolveDocumentDownloadFileName,
  resolveDocumentFileName,
  resolveDocumentUrl,
  triggerDocumentDirectDownload,
} from '@/composables/documents/useDocumentsDownload';
import {
  closeDocumentDeleteModal,
  confirmDocumentDelete,
  openDocumentCreateModal,
  openDocumentDeleteModal,
  openDocumentEditModal,
  resetDocumentModalState,
  submitDocumentCrud,
} from '@/composables/documents/useDocumentsCrud';

const { catalog: text } = useDomainText('documents');

export default {
  name: 'SaleSharedDocumentsTab',
  components: {
    DocumentsTableCard,
    DocumentsCreateModal,
    ConfirmActionModal,
  },
  props: {
    sale: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      text,
      loading: false,
      documentsRows: [],
      showCreateModal: false,
      documentModalMode: 'create',
      editingDocument: null,
      isDeleteConfirmModalOpen: false,
      pendingDeleteDocument: null,
      deletingDocument: false,
    };
  },
  computed: {
    saleId() {
      return Number(this.sale?.id || 0);
    },
    isMultiCustomerSale() {
      const countCandidates = [
        this.sale?.customers_count,
        this.sale?.companies_count,
        this.sale?.clients_count,
      ];

      const hasManyCustomers = countCandidates.some((candidate) => Number(candidate || 0) > 1);
      const hasCustomersArray = Array.isArray(this.sale?.customers) && this.sale.customers.length > 1;
      const multiFlag = Number(this.sale?.is_multi_customer || this.sale?.multi_customer || 0) === 1;
      const typeLabel = String(this.sale?.sale_type || this.sale?.type || this.sale?.sale_mode || '').trim().toLowerCase();

      return hasManyCustomers || hasCustomersArray || multiFlag || typeLabel.includes('multi');
    },
    activeService() {
      return this.isMultiCustomerSale ? salesMultiCustomerService : salesService;
    },
    deleteConfirmMessage() {
      const name = String(this.pendingDeleteDocument?.name || '').trim();
      return name !== ''
        ? this.text.confirmDelete.messageByName(name)
        : this.text.confirmDelete.messageDefault;
    },
    showDownloadAllButton() {
      return this.documentsRows.some(
        (row) => Number(row?.isFile || 0) === 1 && String(row?.documentRaw || '').trim() !== ''
      );
    },
    saleDownloadNameSegment() {
      const saleName = String(this.sale?.name || this.sale?.code || '').trim();
      return normalizeDocumentDownloadNameSegment(saleName, this.text.download.saleFallbackNameSegment);
    },
  },
  watch: {
    saleId: {
      immediate: true,
      handler() {
        this.loadDocuments();
      },
    },
    showCreateModal(visible) {
      if (visible) {
        return;
      }

      resetDocumentModalState(this);
    },
  },
  methods: {
    mapDocumentRow(row) {
      const comments = String(row?.description || '').trim();

      return {
        id: Number(row?.id || 0),
        name: String(row?.name || this.text.common.blankValue).trim() || this.text.common.blankValue,
        validityStart: formatDateShort(row?.start_date, { fallback: this.text.common.blankValue }),
        validityEnd: formatDateShort(row?.end_date, { fallback: this.text.common.blankValue }),
        comments,
        isFile: Number(row?.is_file || 0) === 1 ? 1 : 0,
        documentRaw: String(row?.document || '').trim(),
        documentUrl: String(row?.document_url || '').trim(),
      };
    },
    openCreateModal() {
      openDocumentCreateModal(this);
    },
    async loadDocuments() {
      const saleId = this.saleId;
      if (saleId <= 0) {
        this.documentsRows = [];
        return;
      }

      this.loading = true;
      try {
        const response = await this.activeService.documentsIndex(saleId);
        const payload = extractPayload(response);
        const documents = Array.isArray(payload?.documents) ? payload.documents : [];

        this.documentsRows = documents.map((row) => this.mapDocumentRow(row));
      } catch (error) {
        this.documentsRows = [];
        this.$toast?.error(extractApiErrorMessage(error, this.text.toasts.loadSaleError));
      } finally {
        this.loading = false;
      }
    },
    async handleDownloadAll() {
      if (this.saleId <= 0) {
        return;
      }

      try {
        const response = await this.activeService.documentsDownloadAll(this.saleId);

        const fileName = resolveDocumentDownloadFileName(
          response,
          `${this.text.download.salePrefix}_${this.saleDownloadNameSegment}_${buildDocumentDownloadTimestamp()}.zip`
        );
        downloadBlobFile(response, fileName, 'application/zip');
        this.$toast?.success(this.text.toasts.downloadStarted);
      } catch (error) {
        const message = await resolveDocumentDownloadErrorMessage(error, this.text.toasts.noDocumentsToDownload);
        this.$toast?.error(message);
      }
    },
    async handleDownloadItem(row) {
      const url = resolveDocumentUrl(row);
      if (url === '') {
        this.$toast?.info(this.text.toasts.noDownloadableFile);
        return;
      }

      if (isPdfDocument(row)) {
        openDocumentInNewTab(url);
        return;
      }

      await triggerDocumentDirectDownload(url, resolveDocumentFileName(row));
    },
    handleEditItem(row) {
      openDocumentEditModal(this, row);
    },
    handleDeleteItem(row) {
      openDocumentDeleteModal(this, row);
    },
    closeDeleteConfirmModal() {
      closeDocumentDeleteModal(this);
    },
    async confirmDeleteItem() {
      const documentId = Number(this.pendingDeleteDocument?.id || 0);
      if (this.saleId <= 0 || documentId <= 0) {
        this.closeDeleteConfirmModal();
        return;
      }

      this.deletingDocument = true;
      try {
        const result = await confirmDocumentDelete({
          parentId: this.saleId,
          documentId,
          deleteRequest: (saleId, currentDocumentId) => this.activeService.documentsDelete(saleId, currentDocumentId),
          toast: this.$toast,
          successMessage: this.text.toasts.deleteSuccess,
          errorMessage: this.text.toasts.deleteError,
        });

        if (!result.ok) {
          return;
        }

        this.closeDeleteConfirmModal();
        await this.loadDocuments();
      } finally {
        this.deletingDocument = false;
      }
    },
    async handleCreateDocument(payload) {
      if (this.saleId <= 0) {
        return;
      }

      const name = String(payload?.name || '').trim();
      const sourceType = String(payload?.sourceType || 'file').trim();
      const description = String(payload?.comments || '').trim();
      const startDate = String(payload?.validityStart || '').trim();
      const endDate = String(payload?.validityEnd || '').trim();

      const result = await submitDocumentCrud({
        mode: this.documentModalMode,
        editingDocument: this.editingDocument,
        payload: {
          ...payload,
          name,
          sourceType,
          comments: description,
          validityStart: startDate,
          validityEnd: endDate,
        },
        updateRequest: (documentId, updatePayload) => this.activeService.documentsUpdate(this.saleId, documentId, updatePayload),
        createRequest: (formData, config) => this.activeService.documentsStore(this.saleId, formData, config),
        toast: this.$toast,
        toasts: this.text.toasts,
      });

      if (!result.ok) {
        return;
      }

      try {
        this.showCreateModal = false;
        resetDocumentModalState(this);
        await this.loadDocuments();
      } catch (error) {
        this.$toast?.error(extractApiErrorMessage(error, this.text.toasts.loadSaleError));
      }
    },
  },
};
</script>

<style scoped>
.sale-documents-tab {
  width: 100%;
}
</style>
